import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Input, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { SidebarItemComponent } from '../sidebar-item.component';

@Component({
  selector: 'app-sidebar-desktop',
  standalone: true,
  imports: [CommonModule, SidebarItemComponent],
  templateUrl: './sidebar-desktop.component.html'
})
export class SidebarDesktopComponent {

  showDashboardItems: boolean = false;
  menuTitle: string = 'Dashboard';
  @Input() navigation: any[] = [];
  @Input() name: string = 'mainNavigation';
  private router = inject(Router);
  private _translocoService = inject(TranslocoService);
  private _changeDetectorRef = inject(ChangeDetectorRef);

  private routeTranslationMap: { [key: string]: string } = {
      // Main routes
      dashboard: 'navigation.dashboard',
      players: 'navigation.players.title',
      financials: 'navigation.financials.title',
      'casino-content': 'navigation.casinoContent',
      'sport-content': 'navigation.sportContent',
      'promotional-tools': 'navigation.promotions',
      bonuses: 'promotional.breadcrumbs.bonuses',
      reports: 'navigation.reports.title',
      affiliates: 'navigation.affiliates',
      users: 'navigation.users.title',
      email: 'navigation.email.title',
      settings: 'navigation.settings.title',
      home: 'navigation.dashboard',

      // Financials sub-routes
      cashier: 'navigation.financials.cashier',
      transactions: 'navigation.financials.transactions',
      'exchange-log': 'Exchange Log',
      'verification-queue': 'navigation.players.verification-queue',
      withdrawals: 'navigation.players.withdrawals',

      // Reports sub-routes
      'sport-book': 'navigation.reports.sport-book.title',
      bets: 'navigation.reports.sport-book.bets',
      sports: 'reports.sport-book.sports.title',
      competitions: 'navigation.reports.sport-book.competitions',
      'market-types': 'navigation.reports.sport-book.market-types',
      casino: 'navigation.reports.casino.title',
      game: 'navigation.reports.casino.game',
      player: 'navigation.reports.casino.player',
      provider: 'navigation.reports.casino.provider',
      'player-game': 'navigation.reports.casino.player-game',
      financial: 'navigation.reports.financial.title',
      'payments-report': 'navigation.reports.financial.payments',

      // Users sub-routes
      admins: 'navigation.users.admin',
      'admin-groups': 'navigation.users.admin-group',
      'automatic-groups': 'navigation.users.automatic-admin-group',
      managers: 'navigation.users.managers',
      'manager-groups': 'navigation.users.manager-groups',
      operators: 'navigation.users.operators',
      'operator-groups': 'navigation.users.operator-groups',

      // Email sub-routes
      templates: 'navigation.email.templates',
      logs: 'navigation.email.logs',

      // Casino Management sub-routes (new module, not reports)
      'casino-games': 'navigation.casino.games',
      'casino-categories': 'navigation.casino.categories',
      'casino-providers': 'navigation.casino.providers',
      'casino-logs': 'navigation.casino.auditLogs',

      // Settings sub-routes
      stories: 'navigation.settings.stories',
      offers: 'navigation.settings.offers',
      notifications: 'navigation.settings.notifications',
      highlights: 'navigation.settings.highlights',
      'payment-providers': 'Payment Providers',

      // Template editor sub-routes
      template: 'navigation.template-editor',
      editor: 'settings.template-editor.sports.title',
      markets: 'settings.template-editor.markets.title',

      // Players sub-routes
      'messages-list': 'players.messages-list.title',
      'player-register-sessions': 'players.register-sessions.title',

      // Promotional tools sub-routes
      'bonus-config': 'promotional.breadcrumbs.bonuses',
  };

  ngOnInit(): void {
    const translationsLoaded$ = this._translocoService.selectTranslate(
      'navigation.dashboard'
    );
    translationsLoaded$.subscribe(() => {
        this.updateMenuTitle();
    });

    this.router.events.subscribe(() => {
      this.updateMenuTitle();
      // Auto-close the dashboard menu on route change
      if (this.showDashboardItems) {
          this.showDashboardItems = false;
          this._changeDetectorRef.markForCheck();
      }
  });
  } 

  showDashboard() {
      this.showDashboardItems = !this.showDashboardItems;
  }

  trackByFn(index: number, item: any): any {
    return item?.id ?? index;
  }

  private updateMenuTitle(): void {
      const pathArray = this.router.url.split('/');
      const routeNames = pathArray.filter(Boolean);
      if (routeNames.length > 0) {
          const formattedTitle = routeNames
              .map((name) => this.getTranslatedRouteName(name))
              .join('    /    ');
          this.menuTitle = formattedTitle;
      } else {
          this.menuTitle = this._translocoService.translate(
              'navigation.dashboard'
          );
      }
  }

  private getTranslatedRouteName(routeName: string): string {
      const fullPath = this.router.url;
      const isCasinoManagement = fullPath.startsWith('/casino/') && !fullPath.startsWith('/reports/casino/');

      // Special handling for 'casino' route - check if it's the new casino management module
      // by checking the full URL path
      if (routeName === 'casino') {
          // If it's /casino/* (new module), use casino management translation
          // If it's /reports/casino/* (reports), use reports casino translation
          if (isCasinoManagement) {
              return this._translocoService.translate('navigation.casino.title');
          }
          // Otherwise it's the reports casino
          return this._translocoService.translate('navigation.reports.casino.title');
      }

      // Special handling for casino management sub-routes
      if (isCasinoManagement) {
          const casinoSubRouteMap: { [key: string]: string } = {
              'games': 'navigation.casino.games',
              'categories': 'navigation.casino.categories',
              'providers': 'navigation.casino.providers',
              'logs': 'navigation.casino.auditLogs',
          };
          if (casinoSubRouteMap[routeName]) {
              return this._translocoService.translate(casinoSubRouteMap[routeName]);
          }
      }

      // Check if we have a translation key for this route
      const translationKey = this.routeTranslationMap[routeName];
      if (translationKey) {
          return this._translocoService.translate(translationKey);
      }

      // Fallback to capitalized route name if no translation found
      return this.capitalizeTitle(routeName);
  }

  private capitalizeTitle(title: string): string {
    return title
        .replace(/-/g, ' ')
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
  }

}
